/**
 * Auth Token Storage for Tripsby Studio
 *
 * Handles secure storage of auth tokens and user info.
 * Uses localStorage for persistence across browser reloads.
 *
 * Adapted from concierge-ui/src/lib/auth/token.ts
 * Uses 'studio_' prefix to prevent collision with concierge-ui tokens.
 */

const TOKEN_KEY = 'studio_auth_token'
const USER_KEY = 'studio_user'
const TOKEN_EXPIRY_KEY = 'studio_token_expiry'

export interface AuthUser {
  id: string
  email: string
  name?: string
  picture?: string
}

/**
 * The token's own `exp` claim, in ms. This is what the API will actually
 * enforce, so it is the only expiry worth trusting.
 */
function expiryFromJwt(token: string): number | null {
  const claims = decodeToken(token)
  const exp = claims && typeof claims.exp === 'number' ? claims.exp : null
  return exp ? exp * 1000 : null
}

export function setAuthToken(token: string, expiresIn?: number): void {
  try {
    localStorage.setItem(TOKEN_KEY, token)
    // Prefer the JWT's own exp. The previous order trusted the caller and fell
    // back to a flat 24 hours, so when the exchange returned no expiresIn the
    // app believed it was signed in for a full day while the ~10-hour token had
    // already died. Every request 401'd and the UI just span, because a stale
    // token looks exactly like a valid one until the server refuses it.
    const expiry =
      expiryFromJwt(token) ??
      (expiresIn ? Date.now() + expiresIn * 1000 : Date.now() + 24 * 60 * 60 * 1000)
    localStorage.setItem(TOKEN_EXPIRY_KEY, String(expiry))
  } catch (error) {
    console.error('Failed to store auth token:', error)
  }
}

export function getAuthToken(): string | null {
  try {
    const token = localStorage.getItem(TOKEN_KEY)
    const expiry = localStorage.getItem(TOKEN_EXPIRY_KEY)

    if (!token) return null

    if (expiry && Date.now() > parseInt(expiry, 10)) {
      clearAuthToken()
      return null
    }

    return token
  } catch {
    return null
  }
}

export function setUser(user: AuthUser): void {
  try {
    localStorage.setItem(USER_KEY, JSON.stringify(user))
  } catch (error) {
    console.error('Failed to store user:', error)
  }
}

export function getUser(): AuthUser | null {
  try {
    const userJson = localStorage.getItem(USER_KEY)
    if (!userJson) return null
    return JSON.parse(userJson) as AuthUser
  } catch {
    return null
  }
}

export function clearAuthToken(): void {
  try {
    localStorage.removeItem(TOKEN_KEY)
    localStorage.removeItem(USER_KEY)
    localStorage.removeItem(TOKEN_EXPIRY_KEY)
  } catch (error) {
    console.error('Failed to clear auth:', error)
  }
}

export function isAuthenticated(): boolean {
  return getAuthToken() !== null && getUser() !== null
}

export function decodeToken(token: string): Record<string, unknown> | null {
  try {
    const parts = token.split('.')
    if (parts.length !== 3) return null
    return JSON.parse(atob(parts[1]))
  } catch {
    return null
  }
}

export function getTokenExpiry(): Date | null {
  try {
    const expiry = localStorage.getItem(TOKEN_EXPIRY_KEY)
    if (!expiry) return null
    return new Date(parseInt(expiry, 10))
  } catch {
    return null
  }
}
